/**
 * Sidebar v2 thread row slot parts: the first-line fragments (drop badge,
 * title, pin indicator, status entry, hover actions) the card composes.
 *
 * Split out of sidebarV2Row.tsx (same origin and adaptations) for the
 * file-size ratchet; row assembly and the tooltip chrome stay there.
 *
 * Ported from pingdotgg/t3code `apps/web/src/components/Sidebar.tsx`
 * (SidebarThreadRow card variant) at pin
 * e3e7cc3fc2e02966efe4efc45216dadcd046bfbb (MIT; see
 * THIRD-PARTY-NOTICES.md).
 */
import {
  AlarmClockIcon,
  CheckIcon,
  CircleAlertIcon,
  CircleCheckIcon,
  CircleDashedIcon,
  EyeIcon,
  MessageCircleQuestionIcon,
  PinIcon,
  ShieldQuestionIcon,
} from "lucide-react";

import { cn } from "~/lib/utils";
import { Tooltip, TooltipPopup, TooltipTrigger } from "~/components/ui/tooltip";
import { threadTimeLabel, WorkingDuration } from "./sidebarV2Chrome";
import type { SidebarV2RowModel } from "./sidebarV2RowModel";
import { DROP_VERB_LABEL, SnoozeMenuButton } from "./sidebarV2RowParts";
import { resolveWorkingStartedAt } from "./sidebarV2Status";

/** The lifted row's drop-verb badge, or null while not dragging. */
export function DragDestination(props: {
  readonly dropVerb: string | null;
  readonly dragging: boolean;
}) {
  if (!props.dragging || props.dropVerb === null) return null;
  return (
    <span
      role="status"
      className="pointer-events-none ml-auto inline-flex h-5 shrink-0 items-center gap-1 rounded-sm border border-primary/40 bg-primary/10 px-1.5 text-2xs font-medium text-primary"
    >
      {DROP_VERB_LABEL[props.dropVerb] ?? props.dropVerb}
    </span>
  );
}

/** The title span, or the rename input while renaming. */
export function Title(props: {
  readonly model: SidebarV2RowModel;
  readonly threadTitle: string;
  readonly isRenaming: boolean;
  readonly renamingTitle: string;
  readonly onRenameTitleChange: (title: string) => void;
}) {
  if (props.isRenaming) {
    return (
      <input
        autoFocus
        value={props.renamingTitle}
        aria-label="Thread title"
        onChange={(event) => props.onRenameTitleChange(event.target.value)}
        onFocus={(event) => event.currentTarget.select()}
        onKeyDown={props.model.handleRenameKeyDown}
        onBlur={props.model.handleRenameBlur}
        onClick={(event) => event.stopPropagation()}
        onDoubleClick={(event) => event.stopPropagation()}
        className="min-w-0 flex-1 rounded-sm border border-input bg-card px-1 text-sm font-medium text-card-foreground outline-none focus:border-foreground"
      />
    );
  }
  return (
    <span
      className={cn(
        "min-w-0 flex-1 truncate text-sm transition-opacity motion-reduce:transition-none",
        props.model.shouldRecede ? "font-normal" : "font-medium",
        props.model.shouldRecede ? "text-secondary-label" : "text-foreground/90",
      )}
    >
      {props.threadTitle}
    </span>
  );
}

/** The pinned/unpinned indicator: a button while interactive, a mark while
 * the row is lifted. */
export function PinIndicator(props: {
  readonly dragging: boolean;
  readonly unpin: (event: React.MouseEvent) => void;
}) {
  if (props.dragging) {
    return (
      <PinIcon
        aria-label="Pinned"
        role="img"
        className="size-3 shrink-0 text-muted-foreground/65"
      />
    );
  }
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            aria-label="Unpin thread"
            onClick={props.unpin}
            className="inline-flex cursor-pointer items-center rounded-sm text-muted-foreground/65 outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
          />
        }
      >
        <PinIcon aria-hidden className="size-3 shrink-0" />
      </TooltipTrigger>
      <TooltipPopup>Unpin thread</TooltipPopup>
    </Tooltip>
  );
}

const STATUS_ICONS = {
  working: CircleDashedIcon,
  input: MessageCircleQuestionIcon,
  approval: ShieldQuestionIcon,
  failed: CircleAlertIcon,
  monitoring: EyeIcon,
  woke: AlarmClockIcon,
  done: CircleCheckIcon,
} as const;

/** The card's status entry: the strongest status label, the wake action, or
 * the time label at rest. Yields to the hover actions. */
export function StatusEntry(props: {
  readonly model: SidebarV2RowModel;
  readonly thread: import("~/types").SidebarThreadSummary;
}) {
  const { model } = props;
  if (model.snoozeMenuOpen) {
    return <span className="pointer-events-none absolute right-0 opacity-0" aria-hidden />;
  }
  if (model.topStatus === null) {
    return <span>{threadTimeLabel(props.thread)}</span>;
  }
  if (model.isWokeStatus) {
    return (
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              type="button"
              aria-label="Dismiss Woke notification"
              onClick={model.handleAcknowledgeWokeClick}
              className={cn(
                "inline-flex cursor-pointer items-center gap-1 rounded-sm font-medium outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring",
                model.topStatus.className,
              )}
            >
              <AlarmClockIcon aria-hidden className="size-4 shrink-0" />
              <span role="status">{model.topStatus.label}</span>
            </button>
          }
        />
        <TooltipPopup side="top">Dismiss Woke notification</TooltipPopup>
      </Tooltip>
    );
  }
  const Icon = STATUS_ICONS[model.topStatus.icon];
  return (
    <span className={cn("inline-flex items-center gap-1 font-medium", model.topStatus.className)}>
      {Icon ? <Icon aria-hidden className="size-4 shrink-0" /> : null}
      <span role="status">{model.topStatus.label}</span>
      {model.status === "working" ? (
        <span aria-hidden>
          <WorkingDuration startedAt={resolveWorkingStartedAt(props.thread)} />
        </span>
      ) : null}
    </span>
  );
}

/** The card's hover actions: snooze/settle in the right slot. */
export function HoverActions(props: {
  readonly model: SidebarV2RowModel;
  readonly timestampFormat: import("@cmmd-forge/contracts").TimestampFormat;
  readonly onSnooze: (
    threadRef: import("@cmmd-forge/contracts").ScopedThreadRef,
    preset: { readonly snoozedUntil: string },
  ) => void;
}) {
  const { model } = props;
  return (
    <span
      className={cn(
        "pointer-events-none absolute inset-y-0 right-0 flex items-stretch opacity-0 transition-opacity has-[:focus-visible]:pointer-events-auto has-[:focus-visible]:static has-[:focus-visible]:opacity-100 group-hover/sidebar-row:pointer-events-auto group-hover/sidebar-row:static group-hover/sidebar-row:opacity-100",
        model.snoozeMenuOpen && "pointer-events-auto static opacity-100",
      )}
    >
      {model.snoozeAvailable ? (
        <SnoozeMenuButton
          open={model.snoozeMenuOpen}
          onOpenChange={model.setSnoozeMenuOpen}
          onSnooze={(preset) => props.onSnooze(model.threadRef, preset)}
          timestampFormat={props.timestampFormat}
        />
      ) : null}
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              type="button"
              aria-label="Settle thread"
              onClick={model.settle}
              className="-mr-1 inline-flex cursor-pointer items-center gap-1 rounded-md bg-transparent px-1.5 text-xs text-muted-foreground hover:text-foreground"
            />
          }
        >
          <CheckIcon className="size-3.5" />
          Settle
        </TooltipTrigger>
        <TooltipPopup>Settle thread</TooltipPopup>
      </Tooltip>
    </span>
  );
}
